/**
 * Sales tab tiles (quick-261104-ia3, John 2026-11-04). Every tile uses DashTile
 * so sizes line up; every chart has a plain-English hover.
 */
import ReactECharts from 'echarts-for-react';
import { useMemo, useState } from 'react';

import { BAR_RADIUS, INK, SERIES, baseOption, stageColor, tipHead, tipNote, tipRow } from '../../lib/exec/chart.js';
import { money, num, pct, rate } from '../../lib/exec/fmt.js';
import { FY2027_PIPELINE_TARGET, FY2027_PIPELINE_TARGET_NOTE, sfLink } from '../../lib/exec/links.js';
import { bucketSeries, todayIso, type Grain, type PeriodBucket } from '../../lib/exec/periods.js';
import {
  avgDealByQuarter,
  cycleQuarters,
  dealTypeShort,
  groupColor,
  hasWonAmounts,
  isOtherReason,
  openAvgDeal,
  pooledCycle,
  trailingAvgDeal,
  type CycleType,
  type DealType,
} from '../../lib/exec/sales.js';
import { useMainOppsCreated } from '../../queries/useMainOppsCreated.js';
import { useMainSnap } from '../../queries/useMainSnap.js';

import { BloodDrive, PeriodToggle } from './controls.js';
import { ChangeChip, DashTile, Legend, TileEmpty } from './DashTile.js';
import { SegmentToggle } from './SegmentToggle.js';

import type {
  ExecActivityPanel,
  ExecCoveragePanel,
  ExecCyclePanel,
  ExecDayPoint,
  ExecFunnelPanel,
  ExecWinRatePanel,
  MainScorecardResponse,
} from '@saledash/shared';
import type { EChartsOption } from 'echarts';

const GRAIN_WORD: Record<Grain, string> = { week: 'week', month: 'month', quarter: 'quarter' };

function lastComplete(b: PeriodBucket[]): { cur: PeriodBucket | undefined; prev: PeriodBucket | undefined } {
  const done = b.filter((x) => !x.partial);
  return { cur: done[done.length - 1], prev: done[done.length - 2] };
}

function deltaChip(cur: number | undefined, prev: number | undefined, grain: Grain): React.ReactNode {
  if (cur == null || prev == null || prev === 0) return null;
  const d = (cur - prev) / prev;
  const dir = Math.abs(d) < 0.005 ? 'flat' : d > 0 ? 'up' : 'down';
  return <ChangeChip text={`${d > 0 ? '+' : '−'}${String(Math.round(Math.abs(d) * 100))}%`} dir={dir} suffix={`vs prior ${GRAIN_WORD[grain]}`} />;
}

/** Simple period bar chart (one series or stacked). */
function periodBars(
  buckets: PeriodBucket[],
  series: { name: string; color: string; values: number[] }[],
  tip: (i: number) => string,
  opts: { money?: boolean } = {},
): EChartsOption {
  const base = baseOption({ yMoney: opts.money ?? false });
  return {
    ...base,
    xAxis: { ...(base.xAxis as object), data: buckets.map((b) => (b.partial ? `${b.label}*` : b.label)) },
    tooltip: {
      ...(base.tooltip as object),
      formatter: (params: unknown) => tip((params as { dataIndex: number }[])[0]?.dataIndex ?? 0),
    },
    series: series.map((s, si) => ({
      name: s.name,
      type: 'bar' as const,
      ...(series.length > 1 ? { stack: 'total' } : {}),
      barMaxWidth: 26,
      data: s.values.map((v, i) => ({
        value: v,
        itemStyle: {
          color: s.color,
          opacity: buckets[i]?.partial ? 0.45 : 1,
          borderRadius: si === series.length - 1 ? BAR_RADIUS : 0,
          borderColor: '#fff',
          borderWidth: series.length > 1 ? 1 : 0,
        },
      })),
    })),
  };
}

const PARTIAL_NOTE = '* = period still in progress';

const CYCLE_OPTIONS: { value: CycleType; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New logo' },
  { value: 'exp', label: 'Expansion' },
];
const CYCLE_WORD: Record<CycleType, string> = { all: 'all won deals', new: 'new-logo deals', exp: 'expansion deals' };
const DEAL_OPTIONS: { value: DealType; label: string }[] = [
  { value: 'new', label: 'New logo' },
  { value: 'exp', label: 'Expansion' },
];

function CycleToggle({ value, onChange, disabled }: { value: CycleType; onChange: (v: CycleType) => void; disabled: boolean }): React.JSX.Element | null {
  if (disabled) return null;
  return <SegmentToggle value={value} onChange={onChange} options={CYCLE_OPTIONS} label="Deal type" />;
}

// ─── Sales cycle trend (exec.cycle by type; MAIN-20 fallback) ───────────────
export function CycleTrendTile({
  scorecard,
  cycle,
}: {
  scorecard: MainScorecardResponse | undefined;
  cycle?: ExecCyclePanel | undefined;
}): React.JSX.Element {
  const [sel, setSel] = useState<CycleType>('all');
  const g = scorecard?.grouped['MAIN-20'];
  const hasByType = cycle?.byType != null;
  const panelQs = cycleQuarters(cycle, hasByType ? sel : 'all').slice(-5);
  const usePanel = panelQs.length > 0;
  const pts = useMemo<{ label: string; value: number | null; count: number }[]>(
    () =>
      usePanel
        ? panelQs.map((q) => ({ label: q.label, value: q.all, count: q.allN }))
        : (g?.buckets ?? []).map((b) => ({
            label: b.label,
            value: typeof b.value === 'number' ? b.value : null,
            count: typeof b.count === 'number' ? b.count : 0,
          })),
    [usePanel, panelQs, g],
  );
  const headlineDays = usePanel
    ? sel === 'all' && g?.total_value != null
      ? g.total_value
      : pooledCycle(panelQs, 'all')
    : (g?.total_value ?? null);
  const color = sel === 'exp' ? SERIES.orange : SERIES.blue;
  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ yDays: true, left: 40 });
    return {
      ...base,
      xAxis: { ...(base.xAxis as object), data: pts.map((p) => p.label) },
      tooltip: {
        ...(base.tooltip as object),
        axisPointer: { type: 'line', lineStyle: { color: INK.axis } },
        formatter: (params: unknown) => {
          const p = pts[(params as { dataIndex: number }[])[0]?.dataIndex ?? 0];
          if (!p) return '';
          return (
            tipHead(`${p.label} · ${p.value != null ? `${String(Math.round(p.value))} days` : '—'}`) +
            tipRow(color, 'Deals won', num(p.count)) +
            tipNote('Average days from opportunity created to closed-won, for deals won in that quarter. Lower is faster.')
          );
        },
      },
      series: [
        {
          type: 'line',
          data: pts.map((p) => (p.value != null && p.value > 0 ? Math.round(p.value) : null)),
          connectNulls: true,
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: { width: 2, color },
          itemStyle: { color, borderColor: '#fff', borderWidth: 2 },
          label: { show: true, position: 'top', color: INK.secondary, fontSize: 10, formatter: '{c}d' },
        },
      ],
    };
  }, [pts, color]);
  return (
    <DashTile
      title="Sales cycle trend"
      info="How long it takes to win a deal: average days from opportunity created to closed-won, by the quarter the deal closed. Switch between all deals, new customers only, or expansion with existing customers. A falling line means we are closing faster."
      headline={headlineDays != null ? `${String(Math.round(headlineDays))} days` : '—'}
      sub={`average, last 5 quarters · ${CYCLE_WORD[hasByType ? sel : 'all']}`}
      control={<CycleToggle value={sel} onChange={setSel} disabled={!hasByType} />}
      footer="Won product deals · 880-day Prudential record excluded"
      testId="sales-cycle"
    >
      {pts.length === 0 ? <TileEmpty text="Loading…" /> : <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />}
    </DashTile>
  );
}

// ─── Sales cycle by account size ────────────────────────────────────────────
export function CycleBySizeTile({ cycle }: { cycle: ExecCyclePanel | undefined }): React.JSX.Element {
  const [sel, setSel] = useState<CycleType>('all');
  const hasByType = cycle?.byType != null;
  const qs = cycleQuarters(cycle, hasByType ? sel : 'all').slice(-6);
  const large = pooledCycle(qs, 'large');
  const small = pooledCycle(qs, 'small');
  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ yDays: true, left: 40 });
    return {
      ...base,
      xAxis: { ...(base.xAxis as object), data: qs.map((q) => q.label) },
      tooltip: {
        ...(base.tooltip as object),
        axisPointer: { type: 'line', lineStyle: { color: INK.axis } },
        formatter: (params: unknown) => {
          const q = qs[(params as { dataIndex: number }[])[0]?.dataIndex ?? 0];
          if (!q) return '';
          return (
            tipHead(q.label) +
            tipRow(SERIES.blue, `$500M+ companies (${String(q.largeN)} deals)`, q.large != null ? `${String(Math.round(q.large))} days` : '—') +
            tipRow(SERIES.orange, `Under $500M (${String(q.smallN)} deals)`, q.small != null ? `${String(Math.round(q.small))} days` : '—') +
            tipNote('Bigger companies usually take longer to buy. A gap that keeps widening means large deals are getting stuck.')
          );
        },
      },
      series: [
        { name: '$500M+', type: 'line', data: qs.map((q) => (q.large != null ? Math.round(q.large) : null)), connectNulls: true, symbolSize: 8, lineStyle: { width: 2, color: SERIES.blue }, itemStyle: { color: SERIES.blue, borderColor: '#fff', borderWidth: 2 } },
        { name: 'Under $500M', type: 'line', data: qs.map((q) => (q.small != null ? Math.round(q.small) : null)), connectNulls: true, symbolSize: 8, lineStyle: { width: 2, color: SERIES.orange }, itemStyle: { color: SERIES.orange, borderColor: '#fff', borderWidth: 2 } },
      ],
    };
  }, [qs]);
  return (
    <DashTile
      title="Sales cycle by company size"
      info="The same sales-cycle measure, split by the buyer's annual revenue: companies with $500M or more versus smaller companies. Switch between all deals, new customers or expansion. Hover a quarter to see each group's average days and how many deals it rests on."
      headline={large != null ? `${String(Math.round(large))} vs ${small != null ? String(Math.round(small)) : '—'} days` : '—'}
      sub={`$500M+ vs under $500M, 6-quarter average · ${CYCLE_WORD[hasByType ? sel : 'all']}`}
      control={<CycleToggle value={sel} onChange={setSel} disabled={!hasByType} />}
      footer="Company size = Salesforce Account annual revenue"
      testId="sales-cycle-size"
    >
      {qs.length === 0 ? (
        <TileEmpty text={cycle ? 'No won deals of this type in the window.' : 'Loading…'} />
      ) : (
        <div className="flex h-full flex-col">
          <div className="min-h-0 flex-1">
            <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
          </div>
          <Legend items={[{ label: '$500M+ revenue', color: SERIES.blue }, { label: 'Under $500M', color: SERIES.orange }]} />
        </div>
      )}
    </DashTile>
  );
}

// ─── Outbound activity (stacked by channel) ─────────────────────────────────
export function OutboundTile({ activity }: { activity: ExecActivityPanel | undefined }): React.JSX.Element {
  const [grain, setGrain] = useState<Grain>('week');
  const today = todayIso();
  const parts = useMemo(() => {
    if (!activity) return null;
    const mk = (pts: ExecDayPoint[]): PeriodBucket[] => bucketSeries(pts, grain, today, activity.since);
    return { email: mk(activity.outbound.email), call: mk(activity.outbound.call), linkedin: mk(activity.outbound.linkedin) };
  }, [activity, grain, today]);
  const totals = parts ? parts.email.map((b, i) => b.c + (parts.call[i]?.c ?? 0) + (parts.linkedin[i]?.c ?? 0)) : [];
  const tb: PeriodBucket[] = parts ? parts.email.map((b, i) => ({ ...b, c: totals[i] ?? 0 })) : [];
  const { cur, prev } = lastComplete(tb);
  const option = useMemo<EChartsOption | null>(() => {
    if (!parts) return null;
    return periodBars(
      parts.email,
      [
        { name: 'Email', color: SERIES.blue, values: parts.email.map((b) => b.c) },
        { name: 'Call', color: SERIES.orange, values: parts.call.map((b) => b.c) },
        { name: 'LinkedIn', color: SERIES.aqua, values: parts.linkedin.map((b) => b.c) },
      ],
      (i) =>
        tipHead(`${parts.email[i]?.label ?? ''} · ${num(totals[i])} touches`) +
        tipRow(SERIES.aqua, 'LinkedIn', num(parts.linkedin[i]?.c)) +
        tipRow(SERIES.orange, 'Calls', num(parts.call[i]?.c)) +
        tipRow(SERIES.blue, 'Emails', num(parts.email[i]?.c)) +
        tipNote('Every email, call and LinkedIn touch the sales team logged to prospects in this period.'),
    );
  }, [parts, totals]);
  return (
    <DashTile
      title="Outbound activity"
      info="How much the sales team reached out: emails, calls and LinkedIn touches logged in Salesforce, stacked. Switch between week, month and quarter. A faded bar is the period still in progress."
      headline={cur ? num(cur.c) : '—'}
      sub={<span className="inline-flex items-center gap-1.5">last full {GRAIN_WORD[grain]} {deltaChip(cur?.c, prev?.c, grain)}</span>}
      control={<PeriodToggle value={grain} onChange={setGrain} />}
      size="lg"
      footer={`AEs, BDRs, sales managers and SEs · ${PARTIAL_NOTE}`}
      testId="sales-outbound"
    >
      {!option ? (
        <TileEmpty text="Loading…" />
      ) : (
        <div className="flex h-full flex-col">
          <div className="min-h-0 flex-1">
            <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
          </div>
          <Legend items={[{ label: 'Email', color: SERIES.blue }, { label: 'Call', color: SERIES.orange }, { label: 'LinkedIn', color: SERIES.aqua }]} />
        </div>
      )}
    </DashTile>
  );
}

// ─── Single-series period tile (meetings booked, opps advanced) ─────────────
export function PeriodCountTile({
  title,
  info,
  points,
  since,
  footer,
  testId,
  tipText,
  options = ['week', 'month', 'quarter'],
}: {
  title: string;
  info: string;
  points: ExecDayPoint[] | undefined;
  since: string | undefined;
  footer: string;
  testId: string;
  tipText: string;
  options?: Grain[];
}): React.JSX.Element {
  const [grain, setGrain] = useState<Grain>(options[0] ?? 'week');
  const today = todayIso();
  const b = useMemo(() => (points ? bucketSeries(points, grain, today, since) : null), [points, grain, today, since]);
  const { cur, prev } = b ? lastComplete(b) : { cur: undefined, prev: undefined };
  const option = useMemo<EChartsOption | null>(
    () =>
      b
        ? periodBars(b, [{ name: title, color: SERIES.blue, values: b.map((x) => x.c) }], (i) => tipHead(`${b[i]?.label ?? ''} · ${num(b[i]?.c)}`) + tipNote(tipText))
        : null,
    [b, title, tipText],
  );
  return (
    <DashTile
      title={title}
      info={info}
      headline={cur ? num(cur.c) : '—'}
      sub={<span className="inline-flex items-center gap-1.5">last full {GRAIN_WORD[grain]} {deltaChip(cur?.c, prev?.c, grain)}</span>}
      control={<PeriodToggle value={grain} onChange={setGrain} options={options} />}
      size="lg"
      footer={`${footer} · ${PARTIAL_NOTE}`}
      testId={testId}
    >
      {!option ? <TileEmpty text="Loading…" /> : <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />}
    </DashTile>
  );
}

// ─── Opportunities created (period columns + current stage mix bars) ────────
export function OppsCreatedTile({ activity }: { activity: ExecActivityPanel | undefined }): React.JSX.Element {
  const [grain, setGrain] = useState<Grain>('month');
  const today = todayIso();
  const mix = useMainOppsCreated();
  const b = useMemo(() => (activity ? bucketSeries(activity.oppsCreated, grain, today) : null), [activity, grain, today]);
  // New logo vs expansion split (newer cache rows only); "Other" = any remainder (Type blank / other).
  const split = useMemo(() => {
    if (!activity?.oppsCreatedNew || !activity.oppsCreatedExp || !b) return null;
    const nb = bucketSeries(activity.oppsCreatedNew, grain, today);
    const eb = bucketSeries(activity.oppsCreatedExp, grain, today);
    const newV = b.map((x) => nb.find((y) => y.key === x.key)?.amt ?? 0);
    const expV = b.map((x) => eb.find((y) => y.key === x.key)?.amt ?? 0);
    const newC = b.map((x) => nb.find((y) => y.key === x.key)?.c ?? 0);
    const expC = b.map((x) => eb.find((y) => y.key === x.key)?.c ?? 0);
    const otherV = b.map((x, i) => Math.max(0, x.amt - (newV[i] ?? 0) - (expV[i] ?? 0)));
    return { newV, expV, newC, expC, otherV, hasOther: otherV.some((v) => v > 0.5) };
  }, [activity, b, grain, today]);
  const { cur, prev } = b ? lastComplete(b) : { cur: undefined, prev: undefined };
  const colOption = useMemo<EChartsOption | null>(() => {
    if (!b) return null;
    if (!split) {
      return periodBars(
        b,
        [{ name: '$ created', color: SERIES.blue, values: b.map((x) => x.amt) }],
        (i) =>
          tipHead(`${b[i]?.label ?? ''} · ${money(b[i]?.amt)}`) +
          tipRow(SERIES.blue, 'Opportunities', num(b[i]?.c)) +
          tipNote('New product opportunities opened in this period, in dollars.'),
        { money: true },
      );
    }
    const series = [
      { name: 'New logo', color: SERIES.blue, values: split.newV },
      { name: 'Expansion', color: SERIES.orange, values: split.expV },
      ...(split.hasOther ? [{ name: 'Other', color: SERIES.aqua, values: split.otherV }] : []),
    ];
    return periodBars(
      b,
      series,
      (i) =>
        tipHead(`${b[i]?.label ?? ''} · ${money(b[i]?.amt)} · ${num(b[i]?.c)} opps`) +
        tipRow(SERIES.blue, `New logo (${num(split.newC[i])})`, money(split.newV[i])) +
        tipRow(SERIES.orange, `Expansion (${num(split.expC[i])})`, money(split.expV[i])) +
        (split.hasOther ? tipRow(SERIES.aqua, 'Other type', money(split.otherV[i])) : '') +
        tipNote('New product opportunities opened in this period, in dollars, split into new customers and expansion with existing customers.'),
      { money: true },
    );
  }, [b, split]);
  const stages = (mix.data?.buckets ?? []).filter((s) => s.count > 0);
  const barOption = useMemo<EChartsOption>(() => {
    const base = baseOption();
    return {
      ...base,
      grid: { left: 92, right: 28, top: 4, bottom: 4 },
      xAxis: { type: 'value', show: false },
      yAxis: { type: 'category', inverse: true, data: stages.map((s) => s.stage), axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: INK.secondary, fontSize: 11 } },
      tooltip: {
        ...(base.tooltip as object),
        formatter: (params: unknown) => {
          const s = stages[(params as { dataIndex: number }[])[0]?.dataIndex ?? 0];
          return s ? tipHead(`${s.stage} · ${String(s.count)}`) + tipNote('Where the opportunities created in the last 90 days sit today.') : '';
        },
      },
      series: [
        {
          type: 'bar',
          barWidth: '60%',
          data: stages.map((s) => ({ value: s.count, itemStyle: { color: stageColor(s.stage), borderRadius: [0, 4, 4, 0] } })),
          label: { show: true, position: 'right', color: INK.secondary, fontSize: 10 },
        },
      ],
    };
  }, [stages]);
  return (
    <DashTile
      title="Opportunities created"
      info="Left: dollars of new product opportunities opened each period (switch week, month or quarter), split into new customers and expansion with existing customers. Right: where the opportunities opened in the last 90 days sit today, by stage."
      headline={cur ? money(cur.amt) : '—'}
      sub={<span className="inline-flex items-center gap-1.5">{cur ? `${num(cur.c)} opps, last full ${GRAIN_WORD[grain]}` : ''} {deltaChip(cur?.amt, prev?.amt, grain)}</span>}
      control={<PeriodToggle value={grain} onChange={setGrain} />}
      size="lg"
      footer={`Product record type · right panel: last 90 days, ${num(mix.data?.total_count)} opps / ${money(mix.data?.total_amount)} · ${PARTIAL_NOTE}`}
      testId="sales-opps-created"
    >
      <div className="grid h-full grid-cols-5 gap-3">
        <div className="col-span-3 flex min-h-0 flex-col">
          <div className="min-h-0 flex-1">
            {colOption ? <ReactECharts option={colOption} style={{ height: '100%', width: '100%' }} notMerge /> : <TileEmpty text="Loading…" />}
          </div>
          {split ? (
            <Legend
              items={[
                { label: 'New logo', color: SERIES.blue },
                { label: 'Expansion', color: SERIES.orange },
                ...(split.hasOther ? [{ label: 'Other type', color: SERIES.aqua }] : []),
              ]}
            />
          ) : null}
        </div>
        <div className="col-span-2 flex min-h-0 flex-col border-l border-ink-100 pl-2">
          <div className="text-[0.68rem] font-medium text-ink-500">Where the last 90 days&apos; opps stand now</div>
          <div className="min-h-0 flex-1">
            {stages.length ? <ReactECharts option={barOption} style={{ height: '100%', width: '100%' }} notMerge /> : <TileEmpty text="Loading…" />}
          </div>
        </div>
      </div>
    </DashTile>
  );
}

// ─── Pipeline coverage: CQ / NQ (blood drives) ──────────────────────────────
export function CoverageTile({ scorecard }: { scorecard: MainScorecardResponse | undefined }): React.JSX.Element {
  const m = (id: string): { v: number | null; t: number | null } => ({
    v: scorecard?.metrics[id]?.value ?? null,
    t: scorecard?.metrics[id]?.target_value ?? null,
  });
  const cq = m('MAIN-13');
  const cqM = m('MAIN-14');
  const nq = m('MAIN-15');
  const nqM = m('MAIN-16');
  return (
    <DashTile
      title="Pipeline coverage"
      info="Do we have enough pipeline to hit the number? Each bar fills with open pipeline for that quarter; the dark part is mature pipeline (past Discovery). The black line is the coverage target: three times the quota still left to close. Filling past the line means we are covered."
      headline={cq.v != null && cq.t ? `${(cq.v / (cq.t / 3)).toFixed(1)}×` : '—'}
      sub="this quarter's open pipeline ÷ quota left"
      size="lg"
      footer="Light = all open · dark = mature (Demo and later) · line = 3× quota not yet won"
      testId="sales-coverage"
    >
      {!scorecard ? (
        <TileEmpty text="Loading…" />
      ) : (
        <div className="flex h-full items-end justify-around gap-4 pb-1">
          <BloodDrive label="This quarter" value={cq.v ?? 0} mature={cqM.v} target={cq.t} height={170} />
          <BloodDrive label="Next quarter" value={nq.v ?? 0} mature={nqM.v} target={nq.t} height={170} />
        </div>
      )}
    </DashTile>
  );
}

// ─── Next-year pipeline vs temporary $3.5M target ───────────────────────────
export function NextYearPipelineTile({ coverage }: { coverage: ExecCoveragePanel | undefined }): React.JSX.Element {
  const year = new Date().getFullYear() + 1;
  const cells = (coverage?.cells ?? []).filter((c) => c.year === year);
  const total = cells.reduce((s, c) => s + c.open, 0);
  const mature = cells.reduce((s, c) => s + c.mature, 0);
  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ yMoney: true });
    const qs = [1, 2, 3, 4].map((q) => cells.find((c) => c.quarter === q) ?? { year, quarter: q, open: 0, mature: 0, count: 0 });
    return {
      ...base,
      xAxis: { ...(base.xAxis as object), data: qs.map((q) => `Q${String(q.quarter)}`) },
      tooltip: {
        ...(base.tooltip as object),
        formatter: (params: unknown) => {
          const q = qs[(params as { dataIndex: number }[])[0]?.dataIndex ?? 0];
          return q
            ? tipHead(`Q${String(q.quarter)} ${String(year)} · ${money(q.open)}`) +
                tipRow('#1c5cab', 'Mature (Demo and later)', money(q.mature)) +
                tipRow('#86b6ef', 'Discovery', money(q.open - q.mature)) +
                tipRow(null, 'Deals', num(q.count)) +
                tipNote('Open product deals with a close date in this quarter of next year.')
            : '';
        },
      },
      series: [
        { name: 'Mature', type: 'bar', stack: 'p', barMaxWidth: 34, data: qs.map((q) => q.mature), itemStyle: { color: '#1c5cab', borderColor: '#fff', borderWidth: 1 } },
        { name: 'Discovery', type: 'bar', stack: 'p', barMaxWidth: 34, data: qs.map((q) => q.open - q.mature), itemStyle: { color: '#86b6ef', borderRadius: BAR_RADIUS, borderColor: '#fff', borderWidth: 1 } },
      ],
    };
  }, [cells, year]);
  return (
    <DashTile
      title={`Open pipeline for ${String(year)}`}
      info={`All open product deals with a close date in ${String(year)}, by quarter, filling toward a $3.5M pipeline target. The target is temporary until the ${String(year)} plan is final.`}
      headline={money(total)}
      sub={`${pct(total / FY2027_PIPELINE_TARGET)} of ${money(FY2027_PIPELINE_TARGET)} · ${money(mature)} mature`}
      size="lg"
      footer={FY2027_PIPELINE_TARGET_NOTE}
      testId="sales-next-year"
    >
      {!coverage ? (
        <TileEmpty text="Loading…" />
      ) : (
        <div className="flex h-full gap-3">
          <div className="min-w-0 flex-1">
            <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
          </div>
          <div className="flex w-24 shrink-0 items-end">
            <BloodDrive label={`${String(year)} target`} value={total} mature={mature} target={FY2027_PIPELINE_TARGET} targetNote="temporary" height={190} />
          </div>
        </div>
      )}
    </DashTile>
  );
}

// ─── Win rate: new logo vs expansion ────────────────────────────────────────
export function WinRateTile({ winrate }: { winrate: ExecWinRatePanel | undefined }): React.JSX.Element {
  const qs = (winrate?.quarters ?? []).slice(-7);
  const ytd = winrate?.ytd;
  const nl = ytd ? rate(ytd.newWon, ytd.newLost) : null;
  const ex = ytd ? rate(ytd.expWon, ytd.expLost) : null;
  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ yPercent: true, left: 40 });
    return {
      ...base,
      xAxis: { ...(base.xAxis as object), data: qs.map((q) => q.label) },
      yAxis: { ...(base.yAxis as object), min: 0, max: 1 },
      tooltip: {
        ...(base.tooltip as object),
        axisPointer: { type: 'line', lineStyle: { color: INK.axis } },
        formatter: (params: unknown) => {
          const q = qs[(params as { dataIndex: number }[])[0]?.dataIndex ?? 0];
          if (!q) return '';
          return (
            tipHead(q.label) +
            tipRow(SERIES.blue, `New logo (${String(q.newWon)} won / ${String(q.newWon + q.newLost)})`, pct(rate(q.newWon, q.newLost))) +
            tipRow(SERIES.orange, `Expansion (${String(q.expWon)} won / ${String(q.expWon + q.expLost)})`, pct(rate(q.expWon, q.expLost))) +
            tipNote('Of the deals that closed in the quarter, the share we won. New logo = new customers; expansion = selling more to existing customers.')
          );
        },
      },
      series: [
        { name: 'New logo', type: 'line', data: qs.map((q) => rate(q.newWon, q.newLost)), connectNulls: true, symbolSize: 8, lineStyle: { width: 2, color: SERIES.blue }, itemStyle: { color: SERIES.blue, borderColor: '#fff', borderWidth: 2 } },
        { name: 'Expansion', type: 'line', data: qs.map((q) => rate(q.expWon, q.expLost)), connectNulls: true, symbolSize: 8, lineStyle: { width: 2, color: SERIES.orange }, itemStyle: { color: SERIES.orange, borderColor: '#fff', borderWidth: 2 } },
      ],
    };
  }, [qs]);
  return (
    <DashTile
      title="Win rate"
      info="Of the deals we finished (won or lost), the share we won, shown separately for new customers and for expansion with existing customers. The two are never blended, because they behave very differently. The line shows each quarter."
      headline={
        <span>
          {pct(nl)} <span className="text-sm font-medium text-ink-500">new logo</span> · {pct(ex)}{' '}
          <span className="text-sm font-medium text-ink-500">expansion</span>
        </span>
      }
      sub="this year to date"
      size="lg"
      footer="Closed Won ÷ (Closed Won + Closed Lost), product deals, by stage"
      testId="sales-winrate"
    >
      {qs.length === 0 ? (
        <TileEmpty text="Loading…" />
      ) : (
        <div className="flex h-full flex-col">
          <div className="min-h-0 flex-1">
            <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
          </div>
          <Legend items={[{ label: 'New logo', color: SERIES.blue }, { label: 'Expansion', color: SERIES.orange }]} />
        </div>
      )}
    </DashTile>
  );
}

// ─── Stalled deals trend (MAIN-21 daily snapshots) ──────────────────────────
export function StalledTile({ scorecard }: { scorecard: MainScorecardResponse | undefined }): React.JSX.Element {
  const snap = useMainSnap('MAIN-21', { daysBack: 180 });
  const pts = useMemo(() => {
    const rows = (snap.data?.snapshots ?? []).slice().sort((a, b) => a.snap_date.localeCompare(b.snap_date));
    // One point per week (the last snapshot of each week) keeps the line readable.
    const byWeek = new Map<string, { d: string; v: number }>();
    for (const r of rows) {
      const d = r.snap_date.slice(0, 10);
      const dt = new Date(`${d}T00:00:00Z`);
      dt.setUTCDate(dt.getUTCDate() - dt.getUTCDay());
      byWeek.set(dt.toISOString().slice(0, 10), { d, v: r.value });
    }
    return [...byWeek.values()];
  }, [snap.data]);
  const now = scorecard?.metrics['MAIN-21']?.value ?? null;
  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ left: 32 });
    return {
      ...base,
      xAxis: { ...(base.xAxis as object), data: pts.map((p) => `${String(Number(p.d.slice(5, 7)))}/${String(Number(p.d.slice(8, 10)))}`), boundaryGap: false },
      yAxis: { ...(base.yAxis as object), minInterval: 1 },
      tooltip: {
        ...(base.tooltip as object),
        axisPointer: { type: 'line', lineStyle: { color: INK.axis } },
        formatter: (params: unknown) => {
          const p = pts[(params as { dataIndex: number }[])[0]?.dataIndex ?? 0];
          return p ? tipHead(`Week of ${p.d} · ${String(p.v)} stalled`) + tipNote('Open deals with no stage change and no next-step update for 21+ days. Rising = more deals going quiet.') : '';
        },
      },
      series: [{ type: 'line', data: pts.map((p) => p.v), symbol: 'circle', symbolSize: 6, lineStyle: { width: 2, color: SERIES.red }, itemStyle: { color: SERIES.red }, areaStyle: { color: 'rgba(227,73,72,0.08)' } }],
    };
  }, [pts]);
  return (
    <DashTile
      title="Stalled deals"
      info="Open deals that have gone quiet: no stage change and no next-step update in 21 or more days. The line shows the count week by week; lower is better."
      headline={now != null ? num(now) : '—'}
      sub="open deals quiet 21+ days"
      size="lg"
      footer="Daily snapshot, one point per week · this year's close dates"
      testId="sales-stalled"
    >
      {pts.length < 2 ? <TileEmpty text={snap.isLoading ? 'Loading…' : 'Trend builds as daily snapshots accrue.'} /> : <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />}
    </DashTile>
  );
}

// ─── Slippage (MAIN-26) ─────────────────────────────────────────────────────
export function SlippageTile({ scorecard }: { scorecard: MainScorecardResponse | undefined }): React.JSX.Element {
  const g = scorecard?.grouped['MAIN-26'];
  const b = g?.buckets ?? [];
  const pushedTotal = b.reduce((s, x) => s + (typeof x.value === 'number' ? x.value : 0), 0);
  // Headline = push events (sum of the monthly slipped-event counts); total_value
  // is DISTINCT opps pushed, shown in the sub so the two are never conflated.
  const pushCount = b.reduce((s, x) => s + (typeof x.count === 'number' ? x.count : 0), 0);
  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ yMoney: true });
    return {
      ...base,
      xAxis: { ...(base.xAxis as object), data: b.map((x) => x.label) },
      tooltip: {
        ...(base.tooltip as object),
        formatter: (params: unknown) => {
          const x = b[(params as { dataIndex: number }[])[0]?.dataIndex ?? 0];
          return x
            ? tipHead(`${x.label} · ${money(typeof x.value === 'number' ? x.value : 0)} pushed`) +
                tipRow(SERIES.orange, 'Deals that pushed', num(typeof x.count === 'number' ? x.count : 0)) +
                tipNote('Dollars of open deals whose close date was moved later in that month. Less is better: it means forecasts are holding.')
            : '';
        },
      },
      series: [{ type: 'bar', barMaxWidth: 26, data: b.map((x) => (typeof x.value === 'number' ? x.value : 0)), itemStyle: { color: SERIES.orange, borderRadius: BAR_RADIUS } }],
    };
  }, [b]);
  return (
    <DashTile
      title="Deal slippage"
      info="How often an open deal had its close date pushed later. The headline counts the pushes this year (a deal pushed twice counts twice; the deal count sits beside it); the bars show the dollars pushed out each month. A tall bar means a lot of deals we expected to close got moved out."
      headline={b.length ? `${num(pushCount)} pushes` : '—'}
      sub={b.length ? `${g?.total_value != null ? `${num(g.total_value)} deals · ` : ''}${money(pushedTotal)} pushed out, this year` : ''}
      size="lg"
      footer="Salesforce close-date history, product deals"
      testId="sales-slippage"
    >
      {b.length === 0 ? <TileEmpty text="Loading…" /> : <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />}
    </DashTile>
  );
}

// ─── Loss rate by reason (MAIN-25; "Other" broken out from exec.winrate) ────
export function LossReasonTile({
  scorecard,
  winrate,
}: {
  scorecard: MainScorecardResponse | undefined;
  winrate?: ExecWinRatePanel | undefined;
}): React.JSX.Element {
  const g = scorecard?.grouped['MAIN-25'];
  const b = (g?.buckets ?? []).filter((x) => (typeof x.count === 'number' ? x.count : 0) > 0).sort((x, y) => (Number(y.count) || 0) - (Number(x.count) || 0)).slice(0, 8);
  const groups = (winrate?.lossOther ?? []).filter((x) => x.count > 0).slice().sort((x, y) => y.count - x.count);
  const otherIdx = b.findIndex((x) => isOtherReason(x.label));
  const breakdown = groups.length > 0 && otherIdx >= 0;
  const groupTotal = groups.reduce((s, x) => s + x.count, 0);
  const total = b.reduce((s, x) => s + (Number(x.count) || 0), 0);
  const option = useMemo<EChartsOption>(() => {
    const base = baseOption();
    const counts = b.map((x) => Number(x.count) || 0);
    const series: NonNullable<EChartsOption['series']> = [
      {
        name: 'Reason',
        type: 'bar',
        stack: 'r',
        barWidth: '60%',
        data: counts.map((n, i) => (breakdown && i === otherIdx ? '-' : n)),
        itemStyle: { color: SERIES.red, borderRadius: [0, 4, 4, 0] },
        label: { show: true, position: 'right', color: INK.secondary, fontSize: 10 },
      },
    ];
    if (breakdown) {
      groups.forEach((grp, gi) => {
        const last = gi === groups.length - 1;
        series.push({
          name: grp.group,
          type: 'bar',
          stack: 'r',
          barWidth: '60%',
          data: counts.map((_, i) => (i === otherIdx ? grp.count : '-')),
          itemStyle: { color: groupColor(gi), borderColor: '#fff', borderWidth: 1, borderRadius: last ? [0, 4, 4, 0] : 0 },
          label: last ? { show: true, position: 'right', color: INK.secondary, fontSize: 10, formatter: () => String(groupTotal) } : { show: false },
        });
      });
    }
    return {
      ...base,
      grid: { left: 150, right: 30, top: 4, bottom: 4 },
      xAxis: { type: 'value', show: false },
      yAxis: { type: 'category', inverse: true, data: b.map((x) => x.label), axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: INK.secondary, fontSize: 11, width: 140, overflow: 'truncate' } },
      tooltip: {
        ...(base.tooltip as object),
        formatter: (params: unknown) => {
          const i = (params as { dataIndex: number }[])[0]?.dataIndex ?? 0;
          const x = b[i];
          if (!x) return '';
          if (breakdown && i === otherIdx) {
            return (
              tipHead(`${x.label} · ${String(groupTotal)} deals`) +
              groups.map((grp, gi) => tipRow(groupColor(gi), grp.group, String(grp.count))).join('') +
              tipRow(null, 'Share of losses', pct(total ? (Number(x.count) || 0) / total : null)) +
              tipNote('"Other" losses, grouped from the free-text note written when the deal was closed lost.')
            );
          }
          const n = Number(x.count) || 0;
          return tipHead(`${x.label} · ${String(n)} deals`) + tipRow(null, 'Share of losses', pct(total ? n / total : null)) + tipNote('Why we lost: the reason recorded when a deal was closed lost.');
        },
      },
      series,
    };
  }, [b, total, breakdown, otherIdx, groups, groupTotal]);
  return (
    <DashTile
      title="Why we lose"
      info="Lost deals grouped by the loss reason recorded in Salesforce, most common first. The catch-all “Other” bar is split by what the free-text note says (price, timing, another vendor, going silent and so on). Use it to see what losses are really about."
      headline={num(total)}
      sub="lost deals, top reasons"
      size="lg"
      footer="Salesforce Loss Reason on closed-lost product deals"
      testId="sales-loss"
    >
      {b.length === 0 ? (
        <TileEmpty text="Loading…" />
      ) : (
        <div className="flex h-full flex-col">
          <div className="min-h-0 flex-1">
            <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
          </div>
          {breakdown ? (
            <div className="flex flex-wrap items-center gap-x-2 text-[0.68rem] text-ink-500">
              <span className="font-medium">&ldquo;Other&rdquo; split:</span>
              <Legend items={groups.map((grp, gi) => ({ label: grp.group, color: groupColor(gi) }))} />
            </div>
          ) : null}
        </div>
      )}
    </DashTile>
  );
}

// ─── Average deal size (New logo / Expansion from exec.winrate; MAIN-12 fallback) ─
export function AvgDealTile({
  scorecard,
  winrate,
  funnel,
}: {
  scorecard: MainScorecardResponse | undefined;
  winrate?: ExecWinRatePanel | undefined;
  funnel?: ExecFunnelPanel | undefined;
}): React.JSX.Element {
  if (winrate && hasWonAmounts(winrate.quarters)) return <AvgDealByTypeTile winrate={winrate} funnel={funnel} />;
  return <AvgDealLegacyTile scorecard={scorecard} />;
}

function AvgDealByTypeTile({ winrate, funnel }: { winrate: ExecWinRatePanel; funnel: ExecFunnelPanel | undefined }): React.JSX.Element {
  const [sel, setSel] = useState<DealType>('new');
  const pts = useMemo(() => avgDealByQuarter(winrate.quarters.slice(-7), sel), [winrate, sel]);
  const trailing = trailingAvgDeal(pts, 4);
  const openAvg = openAvgDeal(funnel?.openByType, sel);
  const color = sel === 'new' ? SERIES.blue : SERIES.orange;
  const word = sel === 'new' ? 'new-logo' : 'expansion';
  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ yMoney: true });
    return {
      ...base,
      grid: { ...(base.grid as object), right: 16 },
      xAxis: { ...(base.xAxis as object), data: pts.map((p) => p.label) },
      yAxis: {
        ...(base.yAxis as object),
        ...(openAvg != null ? { max: (v: { max: number }) => Math.max(v.max, openAvg) * 1.1 } : {}),
      },
      tooltip: {
        ...(base.tooltip as object),
        axisPointer: { type: 'line', lineStyle: { color: INK.axis } },
        formatter: (params: unknown) => {
          const p = pts[(params as { dataIndex: number }[])[0]?.dataIndex ?? 0];
          return p
            ? tipHead(p.label) +
                tipRow(color, `Avg won ${word} deal`, money(p.avg)) +
                tipRow(null, 'Deals won', num(p.won)) +
                tipRow(null, 'Won $', money(p.amt)) +
                (openAvg != null ? tipRow(INK.secondary, 'Open deals avg (today)', money(openAvg)) : '') +
                tipNote('Average size of the deals we won that quarter: won dollars divided by deals won. The dashed line is the average open deal of this type right now.')
            : '';
        },
      },
      series: [
        {
          name: 'Avg won deal',
          type: 'line',
          data: pts.map((p) => (p.avg != null && p.avg > 0 ? Math.round(p.avg) : null)),
          connectNulls: true,
          symbolSize: 8,
          lineStyle: { width: 2, color },
          itemStyle: { color, borderColor: '#fff', borderWidth: 2 },
          ...(openAvg != null
            ? {
                markLine: {
                  silent: true,
                  symbol: 'none',
                  lineStyle: { color: INK.secondary, type: 'dashed', width: 1.5 },
                  label: { position: 'insideEndTop', color: INK.secondary, fontSize: 10, formatter: `Open deals avg ${money(openAvg)}` },
                  data: [{ yAxis: openAvg }],
                },
              }
            : {}),
        },
      ],
    };
  }, [pts, openAvg, color, word]);
  return (
    <DashTile
      title="Average deal size"
      info="The average dollar size of the deals we won each quarter, for new customers or for expansion with existing customers (switch at top right). The dashed line is today's average open deal of the same type. The headline is the average over the last four quarters."
      headline={money(trailing)}
      sub={`avg won ${word} deal, last 4 quarters`}
      control={<SegmentToggle value={sel} onChange={setSel} options={DEAL_OPTIONS} label="Deal type" />}
      size="lg"
      footer="Product deals · won $ ÷ deals won, by close quarter"
      testId="sales-avg-deal"
    >
      {pts.length === 0 ? (
        <TileEmpty text="Loading…" />
      ) : (
        <div className="flex h-full flex-col">
          <div className="min-h-0 flex-1">
            <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
          </div>
          <Legend
            items={[
              { label: sel === 'new' ? 'New logo, avg won' : 'Expansion, avg won', color },
              ...(openAvg != null ? [{ label: 'Open deals avg (today)', color: INK.secondary, dashed: true }] : []),
            ]}
          />
        </div>
      )}
    </DashTile>
  );
}

function AvgDealLegacyTile({ scorecard }: { scorecard: MainScorecardResponse | undefined }): React.JSX.Element {
  const g = scorecard?.grouped['MAIN-12'];
  const b = g?.buckets ?? [];
  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ yMoney: true });
    return {
      ...base,
      xAxis: { ...(base.xAxis as object), data: b.map((x) => x.label) },
      tooltip: {
        ...(base.tooltip as object),
        axisPointer: { type: 'line', lineStyle: { color: INK.axis } },
        formatter: (params: unknown) => {
          const x = b[(params as { dataIndex: number }[])[0]?.dataIndex ?? 0];
          return x
            ? tipHead(x.label) +
                tipRow(SERIES.blue, 'Avg won deal', money(typeof x.value === 'number' ? x.value : null)) +
                tipRow(SERIES.aqua, 'Avg open deal', money(typeof x.openValue === 'number' ? x.openValue : null)) +
                tipNote('Average size of deals won that quarter, next to the average open deal at quarter-end. This average is also what prices potential opps.')
            : '';
        },
      },
      series: [
        { name: 'Won', type: 'line', data: b.map((x) => (typeof x.value === 'number' && x.value > 0 ? x.value : null)), connectNulls: true, symbolSize: 8, lineStyle: { width: 2, color: SERIES.blue }, itemStyle: { color: SERIES.blue, borderColor: '#fff', borderWidth: 2 } },
        { name: 'Open', type: 'line', data: b.map((x) => (typeof x.openValue === 'number' ? x.openValue : null)), symbolSize: 6, lineStyle: { width: 2, color: SERIES.aqua, type: 'dashed' }, itemStyle: { color: SERIES.aqua } },
      ],
    };
  }, [b]);
  return (
    <DashTile
      title="Average deal size"
      info="The average dollar size of deals we won each quarter, with the average open deal for comparison. The headline is the going rate the dashboard uses to value potential opps."
      headline={g?.total_value != null ? money(g.total_value) : '—'}
      sub="avg closed-won deal"
      size="lg"
      footer="Product deals · trailing 5 quarters"
      testId="sales-avg-deal"
    >
      {b.length === 0 ? (
        <TileEmpty text="Loading…" />
      ) : (
        <div className="flex h-full flex-col">
          <div className="min-h-0 flex-1">
            <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
          </div>
          <Legend items={[{ label: 'Won', color: SERIES.blue }, { label: 'Open', color: SERIES.aqua, dashed: true }]} />
        </div>
      )}
    </DashTile>
  );
}

// ─── Big deals (exec.funnel.bigDeals; MAIN-11 fallback) ─────────────────────
interface DealBucket {
  oppId: string;
  name: string;
  amount: number;
  stage: string;
  closeDate: string;
  accountName: string;
  ownerName: string;
}
interface BigDealRow {
  id: string;
  name: string;
  account: string;
  type: string;
  amount: number;
  stage: string;
  closeDate: string;
}
export function BigDealsTile({
  scorecard,
  funnel,
}: {
  scorecard: MainScorecardResponse | undefined;
  funnel?: ExecFunnelPanel | undefined;
}): React.JSX.Element {
  const deals: BigDealRow[] = (
    funnel?.bigDeals
      ? funnel.bigDeals.map((d) => ({ id: d.id, name: d.name, account: d.account ?? '', type: dealTypeShort(d.type), amount: d.amount, stage: d.stage, closeDate: d.closeDate }))
      : ((scorecard?.grouped['MAIN-11']?.buckets ?? []) as unknown as DealBucket[]).map((d) => ({
          id: d.oppId,
          name: d.name,
          account: d.accountName,
          type: '—',
          amount: d.amount,
          stage: d.stage,
          closeDate: d.closeDate,
        }))
  )
    .slice()
    .sort((a, b) => b.amount - a.amount);
  const total = deals.reduce((s, d) => s + d.amount, 0);
  return (
    <DashTile
      title="Deals over $200K"
      info="Every open deal worth more than $200K, biggest first, with whether it is a new customer (New) or expansion with an existing one (Exp), its stage and expected close date. These few deals move the quarter."
      headline={money(total)}
      sub={`${num(deals.length)} deals`}
      size="lg"
      footer="Open product deals · click a deal to open it in Salesforce"
      testId="sales-big-deals"
    >
      {deals.length === 0 ? (
        <TileEmpty text={funnel?.bigDeals ? 'No open deals over $200K.' : 'Loading…'} />
      ) : (
        <div className="h-full overflow-y-auto pr-1">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-[0.66rem] uppercase tracking-wider text-ink-400">
                <th className="pb-1 pr-2 font-medium">Deal</th>
                <th className="pb-1 pr-2 font-medium">New / Exp</th>
                <th className="pb-1 pr-2 font-medium">Stage</th>
                <th className="pb-1 pr-2 font-medium">Close</th>
                <th className="pb-1 text-right font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((d) => (
                <tr key={d.id} className="border-t border-ink-100">
                  <td className="max-w-[11rem] truncate py-1.5 pr-2">
                    <a href={sfLink(d.id)} className="text-brand-600 hover:underline" title={d.name}>
                      {d.account || d.name}
                    </a>
                  </td>
                  <td className="py-1.5 pr-2">
                    <span
                      className="font-medium"
                      style={{ color: d.type === 'New' ? SERIES.blue : d.type === 'Exp' ? SERIES.orange : INK.muted }}
                    >
                      {d.type}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2">
                    <span className="inline-flex items-center gap-1 text-ink-600">
                      <span aria-hidden className="inline-block h-2 w-2 rounded-[2px]" style={{ background: stageColor(d.stage) }} />
                      {d.stage}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2 text-ink-500 tabular-nums">{d.closeDate}</td>
                  <td className="py-1.5 text-right font-medium tabular-nums text-ink-900">{money(d.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashTile>
  );
}
